import React, { useEffect, useRef, useState, useMemo } from 'react';
import { MapContainer, Marker, Tooltip, ImageOverlay, useMap } from 'react-leaflet';
import { LatLngTuple } from 'leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Groups, Gateways } from '../../../../Models/Assets/AssetTemplateReadDto';
import AssetTemplateReadDto from '../../../../Models/Assets/AssetTemplateReadDto';
import { GREASE_PUMP_LEAFLET_ICON_URL_BLUE, GREASE_PUMP_LEAFLET_ICON_URL_RED } from '../../../../Utilities/CustomIcons';

/** Derive active state from group status. */
const isGroupActive = (group: Groups): boolean => group.status === 'Active';

/** When true (template mode), exclude groups/grease pumps that are Deleted or Inactive from the image. */
function shouldShowGroupInImage(group: Groups, hideDisabledOrDeleted: boolean): boolean {
  if (!hideDisabledOrDeleted) return true;
  return group.status === 'Active' || (group.status === undefined && group.isEnabled !== false);
}

function shouldShowGreasePumpInImage(pump: { status?: string; isEnabled?: boolean }, hideDisabledOrDeleted: boolean): boolean {
  if (!hideDisabledOrDeleted) return true;
  return pump.status === 'Active' || (pump.status === undefined && pump.isEnabled !== false);
}

function shouldShowGatewayInImage(gw: { status?: string }, hideDisabledOrDeleted: boolean): boolean {
  if (!hideDisabledOrDeleted) return true;
  return gw.status === 'Active' || gw.status === undefined;
}

/** Derive active state from gateway status (for red icon when disabled in asset). */
function isGatewayActive(gw: { status?: string }): boolean {
  return gw.status === 'Active' || gw.status === undefined;
}

/** Derive active state from grease pump status (for red icon when disabled in asset). */
function isGreasePumpActive(pump: { status?: string; isEnabled?: boolean }): boolean {
  return pump.status === 'Active' || (pump.status === undefined && pump.isEnabled !== false);
}

const bluePinSvg = encodeURIComponent(`
  <svg width="30" height="36" viewBox="0 0 37 44" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.5 2C10.5 2 4 8.5 4 16.5C4 28.5 18.5 42 18.5 42C18.5 42 33 28.5 33 16.5C33 8.5 26.5 2 18.5 2Z" fill="#305FE6" stroke="white" stroke-width="1"/>
    <circle cx="18.5" cy="16.5" r="5" fill="white"/>
  </svg>
`);

const redPinSvg = encodeURIComponent(`
  <svg width="30" height="36" viewBox="0 0 37 44" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.5 2C10.5 2 4 8.5 4 16.5C4 28.5 18.5 42 18.5 42C18.5 42 33 28.5 33 16.5C33 8.5 26.5 2 18.5 2Z" fill="#DC2626" stroke="white" stroke-width="1"/>
    <circle cx="18.5" cy="16.5" r="5" fill="white"/>
  </svg>
`);

const BlueIcon = L.icon({
  iconUrl: "data:image/svg+xml," + bluePinSvg,
  iconSize: [30, 36],
  iconAnchor: [15, 36],
});

const RedIcon = L.icon({
  iconUrl: "data:image/svg+xml," + redPinSvg,
  iconSize: [30, 36],
  iconAnchor: [15, 36],
});

const GreasePumpPinIcon = L.icon({
  iconUrl: GREASE_PUMP_LEAFLET_ICON_URL_BLUE,
  iconSize: [30, 36],
  iconAnchor: [15, 36],
});

const GreasePumpPinIconRed = L.icon({
  iconUrl: GREASE_PUMP_LEAFLET_ICON_URL_RED,
  iconSize: [30, 36],
  iconAnchor: [15, 36],
});

/** Gateway pin: blue teardrop with GetGatewayHMEIcon inside (centered like grease pump) */
const gatewayPinSvg = encodeURIComponent(`
  <svg width="30" height="36" viewBox="0 0 37 44" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.5 2C10.5 2 4 8.5 4 16.5C4 28.5 18.5 42 18.5 42C18.5 42 33 28.5 33 16.5C33 8.5 26.5 2 18.5 2Z" fill="#305FE6" stroke="white" stroke-width="1"/>
    <!-- GetGatewayHMEIcon inside the pin - scale 0.42 for larger icon, translate to center -->
    <g transform="translate(10, 7) scale(0.42)">
      <path fill="white" stroke="white" stroke-width="1" d="M 28.838544 17.785683 C 25.623218 17.785683 23.008466 20.400924 23.008466 23.615761 L 23.008466 56.693886 C 23.008466 59.837225 25.508277 62.399977 28.6237 62.514199 L 28.6237 64.266152 C 28.6237 65.278626 29.564669 66.058195 30.959638 66.297402 L 30.959638 70.619667 C 30.959638 71.172401 31.407392 71.619667 31.959638 71.619667 C 32.51237 71.619667 32.959638 71.172401 32.959638 70.619667 L 32.959638 66.37748 L 35.326825 66.37748 L 35.326825 70.619667 C 35.326825 71.172401 35.774093 71.619667 36.326825 71.619667 C 36.87956 71.619667 37.326825 71.172401 37.326825 70.619667 L 37.326825 66.297402 C 38.716621 66.056299 39.652997 65.276353 39.652997 64.266152 L 39.652997 62.523964 L 46.053388 62.523964 L 46.053388 64.266152 C 46.053388 65.278171 46.993439 66.057815 48.387372 66.297402 L 48.387372 70.619667 C 48.387372 71.172401 48.834639 71.619667 49.387372 71.619667 C 49.940106 71.619667 50.387372 71.172401 50.387372 70.619667 L 50.387372 66.37748 L 52.75456 66.37748 L 52.75456 70.619667 C 52.75456 71.172401 53.201826 71.619667 53.75456 71.619667 C 54.307292 71.619667 54.75456 71.172401 54.75456 70.619667 L 54.75456 66.297402 C 56.144355 66.056299 57.080732 65.276353 57.080732 64.266152 L 57.080732 62.449745 C 59.878097 62.023028 62.02995 59.609543 62.02995 56.693886 L 62.02995 23.615761 C 62.02995 20.400924 59.414709 17.785683 56.199872 17.785683 L 28.838544 17.785683 z M 28.838544 20.135292 L 56.199872 20.135292 C 58.118813 20.135292 59.680341 21.69682 59.680341 23.615761 L 59.680341 56.693886 C 59.680341 58.613804 58.118813 60.174355 56.199872 60.174355 L 28.838544 60.174355 C 26.919115 60.174355 25.358075 58.613804 25.358075 56.693886 L 25.358075 23.615761 C 25.358075 21.69682 26.919115 20.135292 28.838544 20.135292 z M 42.518232 33.273964 C 39.135426 33.273964 35.958658 34.586469 33.572919 36.973183 C 33.113937 37.433144 33.113937 38.178263 33.572919 38.637245 C 34.033857 39.096229 34.777998 39.096229 35.236982 38.637245 C 37.178384 36.694866 39.765308 35.625527 42.520185 35.625527 C 45.275062 35.625527 47.861986 36.694866 49.803388 38.637245 C 50.03288 38.866737 50.334638 38.982949 50.635419 38.982949 C 50.936194 38.982949 51.237959 38.866737 51.46745 38.637245 C 51.926434 38.177286 51.926434 37.433144 51.46745 36.973183 C 49.080737 34.587445 45.90299 33.273964 42.520185 33.273964 L 42.518232 33.273964 z M 42.520185 39.008339 C 40.671556 39.008339 38.934243 39.728068 37.62956 41.031777 C 37.170575 41.491736 37.170575 42.23588 37.62956 42.695839 C 37.887737 42.954017 38.237207 43.066002 38.574872 43.03373 C 38.837501 43.008629 39.092817 42.896645 39.293622 42.695839 C 39.349164 42.640236 39.405885 42.585858 39.463544 42.53373 C 41.250985 40.917769 44.022996 40.972149 45.744794 42.695839 C 45.85954 42.810585 45.993696 42.896157 46.135419 42.953652 C 46.17085 42.968026 46.206439 42.981928 46.242841 42.992714 C 46.279243 43.0035 46.31513 43.01091 46.352216 43.018105 C 46.389302 43.025299 46.428012 43.032084 46.465497 43.035683 C 46.502982 43.039282 46.539228 43.039589 46.576825 43.039589 C 46.877606 43.039589 47.179365 42.925331 47.408857 42.695839 C 47.86784 42.23588 47.86784 41.491736 47.408857 41.031777 C 46.104172 39.728068 44.368814 39.008339 42.520185 39.008339 z M 42.520185 44.453652 C 41.808002 44.453652 41.229169 45.03293 41.229169 45.744667 C 41.229169 46.455997 41.808002 47.035683 42.520185 47.035683 C 43.231477 47.035683 43.809247 46.455997 43.809247 45.744667 C 43.809247 45.03293 43.231477 44.453652 42.520185 44.453652 z M 30.84831 53.773964 C 30.166911 53.808606 29.6237 54.375485 29.6237 55.06498 C 29.6237 55.776308 30.202535 56.354042 30.914716 56.354042 C 31.626007 56.354042 32.205732 55.776308 32.205732 55.06498 C 32.205732 54.353244 31.626007 53.773964 30.914716 53.773964 C 30.89246 53.773964 30.87029 53.772847 30.84831 53.773964 z M 38.586591 53.773964 C 37.905193 53.808606 37.361982 54.375485 37.361982 55.06498 C 37.361982 55.776308 37.940815 56.354042 38.652997 56.354042 C 39.36429 56.354042 39.94206 55.776308 39.94206 55.06498 C 39.94206 54.353244 39.36429 53.773964 38.652997 53.773964 C 38.630742 53.773964 38.608572 53.772847 38.586591 53.773964 z M 46.324872 53.773964 C 45.643474 53.808606 45.100263 54.375485 45.100263 55.06498 C 45.100263 55.776308 45.679096 56.354042 46.391278 56.354042 C 47.102571 56.354042 47.680341 55.776308 47.680341 55.06498 C 47.680341 54.353244 47.102571 53.773964 46.391278 53.773964 C 46.369024 53.773964 46.346853 53.772847 46.324872 53.773964 z M 54.0612 53.773964 C 53.379803 53.808606 52.838544 54.375485 52.838544 55.06498 C 52.838544 55.776308 53.415424 56.354042 54.127607 56.354042 C 54.838898 56.354042 55.418622 55.776308 55.418622 55.06498 C 55.418622 54.353244 54.838898 53.773964 54.127607 53.773964 C 54.105351 53.773964 54.083181 53.772847 54.0612 53.773964 z M 30.6237 62.523964 L 37.652997 62.523964 L 37.652997 64.098183 C 37.487958 64.205605 37.057292 64.37748 36.395185 64.37748 L 31.881513 64.37748 C 31.219406 64.37748 30.789227 64.205605 30.6237 64.098183 L 30.6237 62.523964 z M 48.053388 62.523964 L 55.080732 62.523964 L 55.080732 64.098183 C 54.915693 64.205605 54.485027 64.37748 53.822919 64.37748 L 49.3112 64.37748 C 48.649093 64.37748 48.218427 64.205605 48.053388 64.098183 L 48.053388 62.523964 z" transform="translate(-23.008466,-17.785683)"/>
    </g>
  </svg>
`);

const GatewayPinIcon = L.icon({
  iconUrl: "data:image/svg+xml," + gatewayPinSvg,
  iconSize: [30, 36],
  iconAnchor: [15, 36],
});

const gatewayPinSvgRed = encodeURIComponent(`
  <svg width="30" height="36" viewBox="0 0 37 44" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.5 2C10.5 2 4 8.5 4 16.5C4 28.5 18.5 42 18.5 42C18.5 42 33 28.5 33 16.5C33 8.5 26.5 2 18.5 2Z" fill="#DC2626" stroke="white" stroke-width="1"/>
    <!-- GetGatewayHMEIcon inside the pin - scale 0.42 for larger icon, translate to center -->
    <g transform="translate(10, 7) scale(0.42)">
      <path fill="white" stroke="white" stroke-width="1" d="M 28.838544 17.785683 C 25.623218 17.785683 23.008466 20.400924 23.008466 23.615761 L 23.008466 56.693886 C 23.008466 59.837225 25.508277 62.399977 28.6237 62.514199 L 28.6237 64.266152 C 28.6237 65.278626 29.564669 66.058195 30.959638 66.297402 L 30.959638 70.619667 C 30.959638 71.172401 31.407392 71.619667 31.959638 71.619667 C 32.51237 71.619667 32.959638 71.172401 32.959638 70.619667 L 32.959638 66.37748 L 35.326825 66.37748 L 35.326825 70.619667 C 35.326825 71.172401 35.774093 71.619667 36.326825 71.619667 C 36.87956 71.619667 37.326825 71.172401 37.326825 70.619667 L 37.326825 66.297402 C 38.716621 66.056299 39.652997 65.276353 39.652997 64.266152 L 39.652997 62.523964 L 46.053388 62.523964 L 46.053388 64.266152 C 46.053388 65.278171 46.993439 66.057815 48.387372 66.297402 L 48.387372 70.619667 C 48.387372 71.172401 48.834639 71.619667 49.387372 71.619667 C 49.940106 71.619667 50.387372 71.172401 50.387372 70.619667 L 50.387372 66.37748 L 52.75456 66.37748 L 52.75456 70.619667 C 52.75456 71.172401 53.201826 71.619667 53.75456 71.619667 C 54.307292 71.619667 54.75456 71.172401 54.75456 70.619667 L 54.75456 66.297402 C 56.144355 66.056299 57.080732 65.276353 57.080732 64.266152 L 57.080732 62.449745 C 59.878097 62.023028 62.02995 59.609543 62.02995 56.693886 L 62.02995 23.615761 C 62.02995 20.400924 59.414709 17.785683 56.199872 17.785683 L 28.838544 17.785683 z M 28.838544 20.135292 L 56.199872 20.135292 C 58.118813 20.135292 59.680341 21.69682 59.680341 23.615761 L 59.680341 56.693886 C 59.680341 58.613804 58.118813 60.174355 56.199872 60.174355 L 28.838544 60.174355 C 26.919115 60.174355 25.358075 58.613804 25.358075 56.693886 L 25.358075 23.615761 C 25.358075 21.69682 26.919115 20.135292 28.838544 20.135292 z M 42.518232 33.273964 C 39.135426 33.273964 35.958658 34.586469 33.572919 36.973183 C 33.113937 37.433144 33.113937 38.178263 33.572919 38.637245 C 34.033857 39.096229 34.777998 39.096229 35.236982 38.637245 C 37.178384 36.694866 39.765308 35.625527 42.520185 35.625527 C 45.275062 35.625527 47.861986 36.694866 49.803388 38.637245 C 50.03288 38.866737 50.334638 38.982949 50.635419 38.982949 C 50.936194 38.982949 51.237959 38.866737 51.46745 38.637245 C 51.926434 38.177286 51.926434 37.433144 51.46745 36.973183 C 49.080737 34.587445 45.90299 33.273964 42.520185 33.273964 L 42.518232 33.273964 z M 42.520185 39.008339 C 40.671556 39.008339 38.934243 39.728068 37.62956 41.031777 C 37.170575 41.491736 37.170575 42.23588 37.62956 42.695839 C 37.887737 42.954017 38.237207 43.066002 38.574872 43.03373 C 38.837501 43.008629 39.092817 42.896645 39.293622 42.695839 C 39.349164 42.640236 39.405885 42.585858 39.463544 42.53373 C 41.250985 40.917769 44.022996 40.972149 45.744794 42.695839 C 45.85954 42.810585 45.993696 42.896157 46.135419 42.953652 C 46.17085 42.968026 46.206439 42.981928 46.242841 42.992714 C 46.279243 43.0035 46.31513 43.01091 46.352216 43.018105 C 46.389302 43.025299 46.428012 43.032084 46.465497 43.035683 C 46.502982 43.039282 46.539228 43.039589 46.576825 43.039589 C 46.877606 43.039589 47.179365 42.925331 47.408857 42.695839 C 47.86784 42.23588 47.86784 41.491736 47.408857 41.031777 C 46.104172 39.728068 44.368814 39.008339 42.520185 39.008339 z M 42.520185 44.453652 C 41.808002 44.453652 41.229169 45.03293 41.229169 45.744667 C 41.229169 46.455997 41.808002 47.035683 42.520185 47.035683 C 43.231477 47.035683 43.809247 46.455997 43.809247 45.744667 C 43.809247 45.03293 43.231477 44.453652 42.520185 44.453652 z M 30.84831 53.773964 C 30.166911 53.808606 29.6237 54.375485 29.6237 55.06498 C 29.6237 55.776308 30.202535 56.354042 30.914716 56.354042 C 31.626007 56.354042 32.205732 55.776308 32.205732 55.06498 C 32.205732 54.353244 31.626007 53.773964 30.914716 53.773964 C 30.89246 53.773964 30.87029 53.772847 30.84831 53.773964 z M 38.586591 53.773964 C 37.905193 53.808606 37.361982 54.375485 37.361982 55.06498 C 37.361982 55.776308 37.940815 56.354042 38.652997 56.354042 C 39.36429 56.354042 39.94206 55.776308 39.94206 55.06498 C 39.94206 54.353244 39.36429 53.773964 38.652997 53.773964 C 38.630742 53.773964 38.608572 53.772847 38.586591 53.773964 z M 46.324872 53.773964 C 45.643474 53.808606 45.100263 54.375485 45.100263 55.06498 C 45.100263 55.776308 45.679096 56.354042 46.391278 56.354042 C 47.102571 56.354042 47.680341 55.776308 47.680341 55.06498 C 47.680341 54.353244 47.102571 53.773964 46.391278 53.773964 C 46.369024 53.773964 46.346853 53.772847 46.324872 53.773964 z M 54.0612 53.773964 C 53.379803 53.808606 52.838544 54.375485 52.838544 55.06498 C 52.838544 55.776308 53.415424 56.354042 54.127607 56.354042 C 54.838898 56.354042 55.418622 55.776308 55.418622 55.06498 C 55.418622 54.353244 54.838898 53.773964 54.127607 53.773964 C 54.105351 53.773964 54.083181 53.772847 54.0612 53.773964 z M 30.6237 62.523964 L 37.652997 62.523964 L 37.652997 64.098183 C 37.487958 64.205605 37.057292 64.37748 36.395185 64.37748 L 31.881513 64.37748 C 31.219406 64.37748 30.789227 64.205605 30.6237 64.098183 L 30.6237 62.523964 z M 48.053388 62.523964 L 55.080732 62.523964 L 55.080732 64.098183 C 54.915693 64.205605 54.485027 64.37748 53.822919 64.37748 L 49.3112 64.37748 C 48.649093 64.37748 48.218427 64.205605 48.053388 64.098183 L 48.053388 62.523964 z" transform="translate(-23.008466,-17.785683)"/>
    </g>
  </svg>
`);

const GatewayPinIconRed = L.icon({
  iconUrl: "data:image/svg+xml," + gatewayPinSvgRed,
  iconSize: [30, 36],
  iconAnchor: [15, 36],
});

L.Marker.prototype.options.icon = BlueIcon;

interface MarkerData {
  id: string;
  position: {
    xPos: number;
    yPos: number;
  };
  name: string;
  type?: 'group' | 'greasePump' | 'gateway'; // Optional type to distinguish marker types
  isEnabled?: boolean; // For groups: when false, show red icon
}

interface Props {
  onMarkersChange: (markers: MarkerData[]) => void;
  selectedGroupId?: string | null;
  onMarkerSelect?: (groupId: string) => void;
  assetData?: AssetTemplateReadDto;
  assetImageUrl?: string;
  templateName?: string;
  onEditStateChange?: (isEditing: boolean) => void;
  // Grease pump mode props
  mode?: 'groups' | 'greasePump';
  greasePumps?: any[];
  selectedGreasePumpId?: string | null;
  onGreasePumpSelect?: (greasePumpId: string) => void;
  onGreasePumpMarkersChange?: (markers: MarkerData[]) => void;
  onGatewayMarkersChange?: (markers: MarkerData[]) => void;
  // Template mode: always in edit mode, hide edit button
  alwaysEditMode?: boolean;
  instructionText?: string;
  // Hide header when used in template mode (header is shown in parent)
  hideHeader?: boolean;
  /** When true (template session), hide groups/grease pumps that are Deleted or Inactive from the image */
  hideDisabledOrDeleted?: boolean;
}

const calculateMinZoom = (
  imageWidth: number,
  imageHeight: number,
  containerWidth: number,
  containerHeight: number
): number => {
  const zoomX = Math.log2(containerWidth / imageWidth);
  const zoomY = Math.log2(containerHeight / imageHeight);
  return Math.min(zoomX, zoomY);
};

const FitImageMap = ({
  bounds,
  minZoom,
  onReady,
}: {
  bounds: L.LatLngBoundsExpression;
  minZoom: number;
  onReady?: () => void;
}) => {
  const map = useMap();
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (!map || isInitializedRef.current) return;

    // Initial setup
    isInitializedRef.current = true;
    map.setMinZoom(minZoom);
    map.setMaxBounds(bounds);
    map.setZoom(minZoom);
    map.panTo(map.getCenter());

    if (onReady) onReady();

    return () => {
      isInitializedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on initial mount and when map is available
  }, [map]); // Only run on initial mount and when map is available

  // Handle bounds updates separately
  useEffect(() => {
    if (!map || !isInitializedRef.current) return;
    
    map.setMaxBounds(bounds);
  }, [map, bounds]);

  // Handle zoom updates separately
  useEffect(() => {
    if (!map || !isInitializedRef.current) return;

    map.setMinZoom(minZoom);
    if (map.getZoom() !== minZoom) {
      map.setZoom(minZoom);
    }
  }, [map, minZoom]);

  return null;
};

const AssetImageComponent: React.FC<Props> = ({
  onMarkersChange,
  selectedGroupId,
  onMarkerSelect,
  assetData,
  assetImageUrl,
  templateName,
  onEditStateChange,
  // Grease pump mode props
  mode = 'groups',
  greasePumps = [],
  selectedGreasePumpId,
  onGreasePumpSelect,
  onGreasePumpMarkersChange,
  onGatewayMarkersChange,
  // Template mode
  alwaysEditMode = false,
  instructionText,
  hideHeader = false,
  hideDisabledOrDeleted = false
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  const [minZoom, setMinZoom] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [markers, setMarkers] = useState<MarkerData[]>([]);
  // Image/map is read-only; only draggable when alwaysEditMode (e.g. template creation)
  const isDraggable = alwaysEditMode;

  // Track if we've already processed grease pumps to prevent infinite loops
  const processedGreasePumpsRef = useRef<any[]>([]);

  // Notify parent when in template edit mode (alwaysEditMode)
  useEffect(() => {
    if (alwaysEditMode) {
      onEditStateChange?.(true);
    }
  }, [alwaysEditMode, onEditStateChange]);
  
  // Load image and get its dimensions
  useEffect(() => {
    if (!assetImageUrl) return;

    const img = new Image();
    img.src = assetImageUrl;
    img.onload = () => {
      setImageSize({ width: img.width, height: img.height });
    };
  }, [assetImageUrl]);

  // Memoize bounds calculation
  const bounds = useMemo(
    () =>
      [
        [0, 0],
        [imageSize?.height || 0, imageSize?.width || 0],
      ] as [[number, number], [number, number]],
    [imageSize?.height, imageSize?.width]
  );

  // Memoize marker creation - only initialize if markers haven't been set yet (preserve user changes)
  useEffect(() => {
    if (mode === 'greasePump') {
      // In grease pump mode, create markers from both groups (read-only) and grease pumps (editable)
      const allMarkers: MarkerData[] = [];
      
      // Add groups as read-only markers (exclude disabled/deleted in template mode)
      if (assetData?.groups) {
        const groupMarkers: MarkerData[] = assetData.groups
          .filter((group: Groups) => shouldShowGroupInImage(group, hideDisabledOrDeleted))
          .map((group: Groups) => {
          return {
            id: `group_${group.id}`, // Prefix to distinguish from grease pumps
            position: {
              xPos: group.xPos || 0,
              yPos: group.yPos || 0
            },
            name: group.name || `Group ${group.id}`,
            type: 'group',
            isEnabled: isGroupActive(group)
          };
        });
        allMarkers.push(...groupMarkers);
      }
      
      // Add grease pumps as editable markers (exclude disabled/deleted in template mode)
      if (greasePumps && greasePumps.length > 0) {
        const greasePumpMarkers: MarkerData[] = greasePumps
          .filter((pump: any) => shouldShowGreasePumpInImage(pump, hideDisabledOrDeleted))
          .map((greasePump: any) => {
          return {
            id: greasePump.id,
            position: {
              xPos: greasePump.xPos || 0,
              yPos: greasePump.yPos || 0
            },
            name: greasePump.name || `Grease Pump ${greasePump.id}`,
            type: 'greasePump',
            isEnabled: isGreasePumpActive(greasePump)
          };
        });
        allMarkers.push(...greasePumpMarkers);
      }
      
      // Only update if markers have actually changed to prevent infinite loops
      const markersChanged = markers.length !== allMarkers.length ||
        markers.some((marker, index) => {
          const newMarker = allMarkers[index];
          return !newMarker ||
            marker.id !== newMarker.id ||
            marker.name !== newMarker.name ||
            marker.position.xPos !== newMarker.position.xPos ||
            marker.position.yPos !== newMarker.position.yPos ||
            marker.type !== newMarker.type ||
            marker.isEnabled !== newMarker.isEnabled;
        });
      
      if (markersChanged) {
        setMarkers(allMarkers);
      }
    } else {
      // In groups mode, create markers from groups + gateways (exclude disabled/deleted in template mode)
      const newMarkers: MarkerData[] = [];

      if (assetData?.groups) {
        const groupMarkers = assetData.groups
          .filter((group: Groups) => shouldShowGroupInImage(group, hideDisabledOrDeleted))
          .map((group: Groups) => {
            const existingMarker = markers.find(m => m.id === group.id);
            return {
              id: group.id,
              position: {
                xPos: existingMarker?.position.xPos ?? group.xPos ?? 0,
                yPos: existingMarker?.position.yPos ?? group.yPos ?? 0
              },
              name: group.name || `Group ${group.id}`,
              type: 'group' as const,
              isEnabled: isGroupActive(group)
            };
          });
        newMarkers.push(...groupMarkers);
      }

      if (assetData?.gateways && assetData.gateways.length > 0) {
        const gatewayKey = (gw: Gateways) => gw.id || `temp-gw-${gw.gatewayId}`;
        const gatewayMarkers = assetData.gateways
          .filter((gw: Gateways) => shouldShowGatewayInImage(gw, hideDisabledOrDeleted))
          .map((gw: Gateways) => {
            const key = gatewayKey(gw);
            const existingMarker = markers.find(m => m.id === `gateway_${key}`);
            return {
              id: `gateway_${key}`,
              position: {
                xPos: existingMarker?.position.xPos ?? gw.xPos ?? 0,
                yPos: existingMarker?.position.yPos ?? gw.yPos ?? 0
              },
              name: gw.name || `Gateway ${gw.gatewayId}`,
              type: 'gateway' as const,
              isEnabled: isGatewayActive(gw)
            };
          });
        newMarkers.push(...gatewayMarkers);
      }

      if (newMarkers.length === 0 && !assetData?.groups) return;
      if (!hideDisabledOrDeleted && markers.length > 0) return;

      const markersChanged = markers.length !== newMarkers.length ||
        markers.some((m, i) => !newMarkers[i] || m.id !== newMarkers[i].id || m.position.xPos !== newMarkers[i].position.xPos || m.position.yPos !== newMarkers[i].position.yPos || m.isEnabled !== newMarkers[i].isEnabled);
      if (markersChanged) setMarkers(newMarkers);
    }
  }, [mode, assetData?.groups, assetData?.gateways, greasePumps, hideDisabledOrDeleted, assetData?.groups?.map(g => `${g.id}:${g.status}`).join('|'), assetData?.gateways?.map((g: Gateways) => `${g.id || 'temp-gw-' + g.gatewayId}:${g.status ?? ''}`).join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Separate effect to handle greasePumps changes (only in greasePump mode) — must run when status changes (e.g. soft delete)
  useEffect(() => {
    if (mode === 'greasePump' && greasePumps) {
      // Check if grease pumps have actually changed (including status, for soft delete)
      const currentGreasePumps = processedGreasePumpsRef.current;
      const greasePumpsChanged = !currentGreasePumps || 
        currentGreasePumps.length !== greasePumps.length ||
        currentGreasePumps.some((pump, index) => 
          !greasePumps[index] || 
          pump.id !== greasePumps[index].id ||
          pump.status !== greasePumps[index].status ||
          pump.xPos !== greasePumps[index].xPos ||
          pump.yPos !== greasePumps[index].yPos ||
          pump.name !== greasePumps[index].name
        );
      
      if (greasePumpsChanged) {
        processedGreasePumpsRef.current = [...greasePumps];
        setMarkers(prevMarkers => {
          // Keep group markers and update grease pump markers (exclude disabled/deleted in template mode)
          const groupMarkers = prevMarkers.filter(marker => marker.type === 'group');
          const greasePumpMarkers: MarkerData[] = greasePumps
            .filter((pump: any) => shouldShowGreasePumpInImage(pump, hideDisabledOrDeleted))
            .map((greasePump: any) => {
            return {
              id: greasePump.id,
              position: {
                xPos: greasePump.xPos || 0,
                yPos: greasePump.yPos || 0
              },
              name: greasePump.name || `Grease Pump ${greasePump.id}`,
              type: 'greasePump',
              isEnabled: isGreasePumpActive(greasePump)
            };
          });
          return [...groupMarkers, ...greasePumpMarkers];
        });
      }
    }
  }, [mode, greasePumps, hideDisabledOrDeleted, greasePumps?.map((p: any) => `${p.id}:${p.status}`).join('|')]); // eslint-disable-line react-hooks/exhaustive-deps -- status in deps so delete removes marker from image

  // Separate effect to handle groups + gateways changes (only in groups mode)
  useEffect(() => {
    if (mode === 'groups') {
      setMarkers(prevMarkers => {
        const updatedMarkers: MarkerData[] = [];

        if (assetData?.groups) {
          const groupMarkers = assetData.groups
            .filter((group: Groups) => shouldShowGroupInImage(group, hideDisabledOrDeleted))
            .map((group: Groups) => {
              const existingMarker = prevMarkers.find(marker => marker.id === group.id);
              return {
                id: group.id,
                position: {
                  xPos: existingMarker?.position.xPos || group.xPos || 0,
                  yPos: existingMarker?.position.yPos || group.yPos || 0
                },
                name: group.name || `Group ${group.id}`,
                type: 'group' as const,
                isEnabled: isGroupActive(group)
              };
            });
          updatedMarkers.push(...groupMarkers);
        }

        if (assetData?.gateways && assetData.gateways.length > 0) {
          const gatewayKey = (gw: Gateways) => gw.id || `temp-gw-${gw.gatewayId}`;
          const gatewayMarkers = assetData.gateways
            .filter((gw: Gateways) => shouldShowGatewayInImage(gw, hideDisabledOrDeleted))
            .map((gw: Gateways) => {
              const key = gatewayKey(gw);
              const existingMarker = prevMarkers.find(marker => marker.id === `gateway_${key}`);
              return {
                id: `gateway_${key}`,
                position: {
                  xPos: existingMarker?.position.xPos ?? gw.xPos ?? 0,
                  yPos: existingMarker?.position.yPos ?? gw.yPos ?? 0
                },
                name: gw.name || `Gateway ${gw.gatewayId}`,
                type: 'gateway' as const,
                isEnabled: isGatewayActive(gw)
              };
            });
          updatedMarkers.push(...gatewayMarkers);
        }

        const markersChanged = prevMarkers.length !== updatedMarkers.length ||
          prevMarkers.some((prevMarker, index) => {
            const updatedMarker = updatedMarkers[index];
            return !updatedMarker ||
              prevMarker.id !== updatedMarker.id ||
              prevMarker.name !== updatedMarker.name ||
              prevMarker.position.xPos !== updatedMarker.position.xPos ||
              prevMarker.position.yPos !== updatedMarker.position.yPos ||
              (prevMarker as MarkerData).isEnabled !== (updatedMarker as MarkerData).isEnabled;
          });

        return markersChanged ? updatedMarkers : prevMarkers;
      });
    }
  }, [mode, assetData?.groups, assetData?.gateways, hideDisabledOrDeleted]); // eslint-disable-line react-hooks/exhaustive-deps -- only run when groups/gateways change

  // Optimize ResizeObserver effect
  useEffect(() => {
    if (!containerRef.current || !imageSize) return;

    const handleResize = (entries: ResizeObserverEntry[]) => {
      const entry = entries[0];
      const { width, height } = entry.contentRect;
      const newMinZoom = calculateMinZoom(
        imageSize.width,
        imageSize.height,
        width,
        height
      );

      const zoomOffset = 0.05;
      const adjustedZoom = newMinZoom - zoomOffset;

      // Only update if the zoom value has actually changed significantly to prevent infinite loops
      setMinZoom((prev) => {
        if (prev === null || Math.abs(prev - adjustedZoom) > 0.01) {
          return adjustedZoom;
        }
        return prev;
      });
    };

    const observer = new ResizeObserver(handleResize);
    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [imageSize]);

  const ready = imageSize && minZoom !== null;

  const handleDragEnd = (id: string, e: any) => {
    const { lat, lng } = e.target.getLatLng();
    
    const newMarkers = markers.map((marker) =>
      marker.id === id
        ? { ...marker, position: { xPos: lng, yPos: lat } }
        : marker
    );
    
    setMarkers(newMarkers);
    
    // Call appropriate handler based on mode and marker type
    if (mode === 'greasePump') {
      const greasePumpMarkers = newMarkers.filter(marker => marker.type === 'greasePump');
      onGreasePumpMarkersChange?.(greasePumpMarkers);
    } else if (id.startsWith('gateway_')) {
      const gatewayMarkers = newMarkers.filter(marker => marker.type === 'gateway');
      onGatewayMarkersChange?.(gatewayMarkers);
    } else {
      onMarkersChange(newMarkers);
    }
  };

  const handleMarkerClick = (id: string, markerType?: string) => {
    // Gateways are not selectable
    if (markerType === 'gateway') return;
    // In grease pump mode, only allow clicking on grease pumps (not groups)
    if (mode === 'greasePump') {
      if (markerType === 'greasePump') {
        onGreasePumpSelect?.(id);
      }
    } else {
      onMarkerSelect?.(id);
    }
  };

  const markerElements = useMemo(
    () =>
      markers.map((marker) => {
        // Determine selection state based on mode and marker type
        const selectedId = mode === 'greasePump' ? selectedGreasePumpId : selectedGroupId;
        const isSelected = selectedId === marker.id;
        
        const isGroup = marker.type === 'group';
        const isGateway = marker.type === 'gateway';
        const isDraggableMarker = mode === 'greasePump' ? !isGroup && isDraggable : isDraggable;
        
        // Determine icon: red when disabled in asset (group/gateway/grease pump), blue when enabled
        const markerIcon = isGateway
          ? (marker.isEnabled === false ? GatewayPinIconRed : GatewayPinIcon)
          : isGroup
            ? (marker.isEnabled === false ? RedIcon : BlueIcon)
            : (marker.isEnabled === false ? GreasePumpPinIconRed : GreasePumpPinIcon);
        // Gateways are not selectable so always opacity 1
        const markerOpacity = isGateway ? 1 : (mode === 'greasePump' ? (isGroup ? 0.7 : 1) : (selectedId === null ? 1 : (isSelected ? 1 : 0.6)));
        
        return (
        <Marker
          key={marker.id}
          position={[marker.position.yPos, marker.position.xPos] as LatLngTuple}
          icon={markerIcon}
          {...{ opacity: markerOpacity } as any}
          draggable={isDraggableMarker}
          eventHandlers={{
            click: () => handleMarkerClick(marker.id, marker.type),
            dragend: (e) => handleDragEnd(marker.id, e)
          }}
        >
          <Tooltip 
            direction="top" 
            offset={[0, -20]}
            className={isSelected ? "selected-marker-tooltip" : ""}
          >
            <div className={`text-center font-semibold ${isSelected ? 'text-[#305FE6]' : ''}`}>
              {marker.name}
            </div>
            {/* xPos and yPos to create templates */}
            {/* <div className="text-center font-semibold">
              xPos: {marker.position.xPos}
            </div>
            <div className="text-center font-semibold">
              yPos: {marker.position.yPos}
            </div> */}
          </Tooltip>
        </Marker>
      )
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run when markers, selectedGroupId, selectedGreasePumpId, mode, onMarkerSelect, onGreasePumpSelect, isDraggable change
    [markers, selectedGroupId, selectedGreasePumpId, mode, onMarkerSelect, onGreasePumpSelect, isDraggable]
  );

  // Memoize map component to prevent unnecessary re-renders
  const mapComponent = useMemo(() => {
    if (!ready) return null;

    return (
      <MapContainer
        crs={L.CRS.Simple}
        zoom={minZoom!}
        minZoom={minZoom!}
        maxZoom={4}
        dragging={true}
        doubleClickZoom={false}
        scrollWheelZoom={true}
        zoomControl={false}
        style={{ height: '100%', width: '100%', backgroundColor: 'transparent' }}
        bounds={bounds}
        maxBounds={bounds}
        zoomSnap={0.1}
        zoomAnimation={false}
        fadeAnimation={false}
      >
        <ImageOverlay url={assetImageUrl!} bounds={bounds} />
        <FitImageMap bounds={bounds} minZoom={minZoom!} onReady={() => setLoading(false)} />
        {markerElements}
      </MapContainer>
    );
  }, [ready, minZoom, bounds, assetImageUrl, markerElements]);

  return (
    <div className="h-full w-full flex flex-col">
      {!hideHeader && (
        <div className="flex justify-between align-middle mb-2">
          <div>
            <div className="text-xl font-bold">{templateName}</div>
            {instructionText && (
              <p className="text-xs text-gray-500 mt-1">{instructionText}</p>
            )}
          </div>
        </div>
      )}
      <div className="flex-grow relative" ref={containerRef}>
        {loading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white bg-opacity-60">
            <div className="loader border-4 border-gray-300 border-t-blue-500 rounded-full w-12 h-12 animate-spin" />
          </div>
        )}
        {mapComponent}
      </div>
    </div>
  );
};

export default AssetImageComponent;